import { useQuery, useMutation } from "@tanstack/react-query";
import { Contract, JsonRpcProvider, FallbackProvider } from "ethers";
import { erc20Abi } from "viem";

import { toast } from "sonner";
// import { solanaWallets } from "./utils/solana-adapter";
import {
  PublicKey as SolanaPublicKey,
  ComputeBudgetProgram,
  Transaction as SolanaTransaction,
  AddressLookupTableProgram,
  TransactionMessage,
  VersionedTransaction,
  Connection,
  clusterApiUrl,
  TransactionInstruction as SolanaTransactionInstruction,
  SystemProgram,
  Keypair as SolanaKeypair,
} from "@solana/web3.js";
import { AnchorProvider, BN, Idl, Program } from "@coral-xyz/anchor";
import { isMainnet } from "based-shared/constants";
import { getProviders, signIn, signOut, useSession } from "next-auth/react";
import {
  WalletAdapterNetwork,
  WalletName,
  WalletReadyState,
} from "@solana/wallet-adapter-base";
import { NetworkSymbols } from "based-auth/networks";
import { routes, useSimpleKitContext } from "./context/SimpleKitContext";
import { IChain } from "based-auth/chains";
import { getEthersProvider } from "./utils/adapters";
import {
  connect as wagmiConnect,
  getAccount as wagmiGetAccount,
  getChainId as wagmiGetChainId,
  disconnect as wagmiDisconnect,
  switchChain as wagmiSwitchChain,
  signMessage as wagmiSignMessage,
  getConnectors as wagmiGetConnectors,
  writeContract as wagmiWriteContract,
  ConnectorAlreadyConnectedError,
} from "@wagmi/core";
import { WrongChainLogo } from "based-assets";
import { useWallet } from "@solana/wallet-adapter-react";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { MetaMaskLogo, CoinbaseLogo, WalletConnectLogo } from "based-assets";
import { deserialize, serialize } from "based-auth/utils";
import { useEffect } from "react";
import { Wallet } from "@solana/wallet-adapter-react";

type CustomUser = {
  address?: string;
  chainId?: number;
  social?: string;
  image?: string;
  name?: string;
  role?: string;
  network?: string;
  provider?: string;
  users?: any[];
};

const maxTxSize = 1232;
const limitOnTable = 27;

function estimateTransactionSize(tx: SolanaTransaction): number {
  const messageBytes = tx.serializeMessage(); // No signature
  const numSignatures = tx.signatures.length || 1; // 1 or more
  const SIGNATURE_SIZE = 64; // Each signature is 64 bytes
  const SIGNATURE_HEADER_SIZE = 1; // Compact-u16 length prefix

  return (
    SIGNATURE_HEADER_SIZE + numSignatures * SIGNATURE_SIZE + messageBytes.length
  );
}
``;
export function useAccount(): {
  chainId: number;
  network: string;
  address: string;
  social: string;
  name: string;
  image: string;
  role: string;
  provider: string;
  users: any[];
  chain: IChain;
  switchChain: (chainId: number) => Promise<void>;
  switchAccount: (network: string, address: string) => Promise<void>;
} {
  const { chains } = useSimpleKitContext();
  const { data: session, update } = useSession();
  const {
    chainId = 0,
    network = "",
    address = "",
    social = "",
    name = "",
    image = "",
    role = "",
    provider = "",
    users = [],
  } = (session?.user as CustomUser) || {};
  const chain = chains.find((item) => item.id === chainId);
  return {
    address,
    chainId,
    social,
    image,
    name,
    role,
    network,
    provider,
    users,
    chain,
    switchChain: async (chainId: number) => {
      await update({
        id: "switchChain",
        chainId,
      });
    },
    switchAccount: async (network: string, address: string) => {
      await update({
        id: "switchAccount",
        network,
        address,
      });
    },
  };
}

export function useChainInfo() {
  const { chainId } = useAccount();
  const { chains } = useSimpleKitContext();
  const chain = chains.find((item) => item.id === chainId);
  return {
    chainLogo: chain?.logo || WrongChainLogo,
    networkLogo: chain?.network?.logo || WrongChainLogo,
    nativeSymbol: chain?.nativeCurrency?.symbol || "",
    nativeDecimals: chain?.nativeCurrency?.decimals || 18,
    txExplorer: chain?.blockExplorers?.default.hash || "",
    addressExplorer: chain?.blockExplorers?.default.address || "",
  };
}

export function useSocialProviders(): UseQueryResult<any, unknown> {
  return useQuery({
    queryKey: ["use-social-providers"],
    queryFn: async () => {
      const connectors = await getProviders();
      return Object.values(connectors || {}).filter(
        (provider) => provider.id !== "credentials"
      );
    },
  });
}

export function useEthWallets(): UseQueryResult<any, unknown> {
  const { wagmiConfig } = useSimpleKitContext();
  const { data, ...rest } = useQuery({
    queryKey: ["use-eth-wallets"],
    queryFn: async () => {
      const ethWalletLogos = {
        metaMask: MetaMaskLogo,
        walletConnect: WalletConnectLogo,
        coinbaseWalletSDK: CoinbaseLogo,
      };
      const connectors = await wagmiGetConnectors(wagmiConfig);
      return (
        connectors
          .map((item: any) => {
            item.isInstalled =
              item.type === "mock" ||
              (item.type === "injected" && item.id !== "metaMask") ||
              item.id === "coinbaseWalletSDK";
            item.icon =
              ethWalletLogos[item.id as keyof typeof ethWalletLogos] ||
              item.icon;
            return item;
          })
          // remove duplicate ids
          .filter(
            (wallet: any, index: number, self: any) =>
              self.findIndex((w: any) => w.id === wallet.id) === index
          )
          // Replace walletConnect's name with the one from options
          .map((wallet: any) => {
            if (wallet.id === "walletConnect") {
              return {
                ...wallet,
                name: "WalletConnect",
              };
            }
            return wallet;
          })
          // remove wallet with id coinbaseWalletSDK if wallet with id 'com.coinbase.wallet' exists
          .filter(
            (wallet: any, index: number, self: any) =>
              !(
                wallet.id === "coinbaseWalletSDK" &&
                self.find((w: any) => w.id === "com.coinbase.wallet")
              )
          )
          .filter(
            (wallet: any, index: number, self: any) =>
              !(
                (wallet.id === "metaMaskSDK" || wallet.id === "metaMask") &&
                self.find(
                  (w: any) =>
                    w.id === "io.metamask" || w.id === "io.metamask.mobile"
                )
              )
          )
          // order by isInstalled injected connectors first
          .sort((a: any, b: any) => {
            const AisInstalled = a.isInstalled && a.type === "injected";
            const BisInstalled = b.isInstalled && b.type === "injected";

            if (AisInstalled && !BisInstalled) return -1;
            if (!AisInstalled && BisInstalled) return 1;
            return 0;
          })
          // move walletConnect to the end
          .sort((a: any, b: any) => {
            if (a.id === "walletConnect") return 1;
            if (b.id === "walletConnect") return -1;
            return 0;
          })
      );
    },
  });
  return { ...rest, data: data || [] };
}

export function useSolanaWallet() {
  const {
    wallet,
    publicKey,
    wallets: solWallets,
    connect: solConnect,
    disconnect: solDisconnect,
    select: solSelect,
    signMessage: solSignMessage,
    signTransaction: solSignTransaction,
    signAllTransactions: solSignAllTransactions,
  } = useWallet();
  return {
    wallet: wallet as Wallet,
    publicKey,
    solConnect,
    solDisconnect,
    solSelect,
    solSignMessage,
    solSignTransaction,
    solSignAllTransactions,
    solWallets: solWallets
      .filter(
        (item: any) =>
          item.readyState === WalletReadyState.Installed ||
          item.readyState === WalletReadyState.Loadable
      )
      .sort((_, cur: any) =>
        cur.readyState === WalletReadyState.Installed ? 1 : -1
      )
      .map((item) => item.adapter),
  };
}

export function getProvider(
  chainId: number,
  chains: IChain[],
  wagmiConfig?: any,
  isSignedIn?: boolean
): JsonRpcProvider | FallbackProvider | Connection | undefined {
  const chain = chains.find((item) => item.id === chainId);

  if (!chain) {
    throw new Error("Chain not found");
  }

  let provider: JsonRpcProvider | FallbackProvider | Connection | undefined;

  switch (chain.network.symbol) {
    case NetworkSymbols.eth:
      provider = !isSignedIn
        ? new JsonRpcProvider(chain.rpcUrls.default.http[0])
        : getEthersProvider(wagmiConfig, { chainId });
      break;
    case NetworkSymbols.sol:
      provider = new Connection(
        clusterApiUrl(
          isMainnet ? WalletAdapterNetwork.Mainnet : WalletAdapterNetwork.Devnet
        ),
        "confirmed"
      );
      break;
  }
  return provider;
}

export function useBalance({
  address: queryAddress,
  chainId: queryChainId,
}: {
  address?: string;
  chainId?: number;
} = {}): UseQueryResult<bigint, unknown> {
  const { chainId: accountChainId, address: accountAddress } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();
  const chainId = queryChainId || accountChainId;
  const chain = chains.find((item) => item.id === chainId);

  const address = queryAddress || accountAddress;
  const { data: balance = 0n, ...rest } = useQuery({
    queryKey: [
      "use-account-balance",
      {
        chainId,
        address,
      },
    ],
    queryFn: async function () {
      if (!address) {
        return 0n;
      }
      if (!chain) {
        throw new Error("Chain not found");
      }
      const provider = getProvider(
        chainId,
        chains,
        wagmiConfig,
        address !== ""
      );
      switch (chain.network.symbol) {
        case NetworkSymbols.eth:
          if (
            provider instanceof JsonRpcProvider ||
            provider instanceof FallbackProvider
          ) {
            return await provider.getBalance(address);
          }
          return 0n;
        case NetworkSymbols.sol:
          if (provider instanceof Connection) {
            const lamports = await provider.getBalance(
              new SolanaPublicKey(address)
            );
            return BigInt(lamports);
          }
          return 0n;
        default:
          return 0n;
      }
    },
  });

  return { ...rest, data: balance } as UseQueryResult<bigint, unknown>;
}

export function useTokenBalance({
  address: queryAddress,
  tokenAddress,
  chainId: queryChainId,
}: {
  address?: string;
  tokenAddress: string;
  chainId?: number;
}): UseQueryResult<bigint, unknown> {
  const { chainId: accountChainId, address: accountAddress } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();
  const chainId = queryChainId || accountChainId;
  const chain = chains.find((item) => item.id === chainId);

  const address = queryAddress || accountAddress;

  const { data: balance = 0n, ...rest } = useQuery({
    queryKey: [
      "use-token-balance",
      {
        chainId,
        address,
        tokenAddress,
      },
    ],
    queryFn: async function () {
      if (!address) {
        return 0n;
      }
      if (!chain) {
        throw new Error("Chain not found");
      }
      const provider = getProvider(
        chainId,
        chains,
        wagmiConfig,
        address !== ""
      );
      switch (chain.network.symbol) {
        case NetworkSymbols.eth:
          if (
            provider instanceof JsonRpcProvider ||
            provider instanceof FallbackProvider
          ) {
            const contract = new Contract(tokenAddress, erc20Abi, provider);
            return await contract.balanceOf(address);
          }
          return 0n;
        case NetworkSymbols.sol:
          if (provider instanceof Connection) {
            const tokenAccount = await provider.getTokenAccountsByOwner(
              new SolanaPublicKey(address),
              { mint: new SolanaPublicKey(tokenAddress) }
            );
            if (tokenAccount.value.length === 0) return 0n;
            const accountInfo = await provider.getTokenAccountBalance(
              tokenAccount.value[0].pubkey
            );
            return BigInt(accountInfo.value.amount);
          }
          return 0n;
        default:
          return 0n;
      }
    },
  });

  return { ...rest, data: balance };
}

export function useConnectAccount() {
  const { chains, pendingItem, wagmiConfig, setRouter, setPendingItem } =
    useSimpleKitContext();

  const { solWallets } = useSolanaWallet();
  const { data: ethWallets } = useEthWallets();
  const { data: socialProviders } = useSocialProviders();

  return useMutation({
    mutationKey: [
      "use-connect",
      {
        id: pendingItem?.id,
        network: pendingItem?.network,
      },
    ],
    mutationFn: async () => {
      if (!pendingItem) {
        return;
      }
      if (pendingItem.network === NetworkSymbols.eth) {
        const connector = ethWallets.find((w: any) => w.id === pendingItem.id);
        if (!connector) {
          throw new Error(`Wallet ${pendingItem.id} not found`);
        }
        try {
          await wagmiDisconnect(wagmiConfig, {
            connector,
          });
        } catch {}

        let result: {
          chainId: number;
          accounts: string[];
        } | null = null;
        try {
          const connectResult = await wagmiConnect(wagmiConfig, {
            connector,
          });
          result = {
            chainId: connectResult.chainId,
            accounts: Array.from(connectResult.accounts),
          };
        } catch (error) {
          if (!(error instanceof ConnectorAlreadyConnectedError)) {
            console.log("error", error);
            throw error;
          }
        }

        if (!result) {
          const chainId = wagmiGetChainId(wagmiConfig);
          const accountResult = wagmiGetAccount(wagmiConfig);
          result = {
            chainId,
            accounts: accountResult?.address ? [accountResult.address] : [],
          };
        }

        if (!result?.chainId) {
          throw new Error("Failed to connect to wallet");
        }

        const chain = chains.find((c) => c.id === result!.chainId);

        if (!chain) {
          const newChain = chains.find(
            (c) => c.network.symbol === NetworkSymbols.eth
          );
          if (!newChain) {
            throw new Error("Chain not found");
          }
          await wagmiSwitchChain(wagmiConfig, {
            connector,
            chainId: newChain.id,
          });
        }

        setRouter(() => {
          setPendingItem({
            id: connector?.id,
            name: connector?.name,
            logo: connector?.icon,
            network: NetworkSymbols.eth,
            chainId: chain!.id,
            address: result?.accounts?.[0],
          });
          return routes.accountSigning;
        });
      } else if (pendingItem.network === NetworkSymbols.sol) {
        const connector = solWallets.find(
          (w: any) => w.name === pendingItem.id
        );
        if (!connector) {
          throw new Error(`Wallet ${pendingItem.id} not found`);
        }
        if (connector.connected) {
          await connector.disconnect();
        }
        await connector.connect();
      } else {
        const provider = socialProviders.find(
          (p: any) => p.id === pendingItem.id
        );
        if (!provider) {
          throw new Error(`Provider ${pendingItem.id} not found`);
        }
        // const authWindow = popupWindow(
        //   `/auth/${provider.id}`,
        //   "Connect Social Wallet"
        // );
        // if (!authWindow) {
        //   throw new Error("Popup blocked");
        // }
        // await new Promise((resolve) => {
        //   const checkClosed = setInterval(() => {
        //     if (authWindow.closed) {
        //       clearInterval(checkClosed);
        //       resolve(true);
        //     }
        //   }, 500);
        // });
        await signIn(provider.id);
        toast.success("Redirecting to oauth provider");
      }
    },
    onSuccess: () => {
      toast.success("Connected to wallet");
    },
    onError: (error: any) => {
      console.log("connect error", error);
      throw error;
    },
  });
}

export function useSwitchChain(): UseMutationResult<void, unknown, number> {
  const {
    address,
    social,
    chain,
    network,
    provider,
    switchChain: switchChainAccount,
  } = useAccount();

  const { wagmiConfig } = useSimpleKitContext();
  const { data: ethWallets } = useEthWallets();

  return useMutation({
    mutationKey: [
      "use-switch-chain",
      { social, network: chain?.network?.symbol },
    ],
    mutationFn: async (chainId: number) => {
      if (!address) {
        return;
      }
      if (!social) {
        if (network === NetworkSymbols.eth) {
          const connector = ethWallets.find((w: any) => w.id === provider);
          if (!connector) {
            throw new Error(`Wallet ${provider} not found`);
          }
          await wagmiSwitchChain(wagmiConfig, {
            connector,
            chainId,
          });
        } else if (network === NetworkSymbols.sol) {
          throw new Error("Solana wallet does not support chain switching");
        } else {
          throw new Error("Unknown network");
        }
      } else {
        await switchChainAccount(chainId);
      }
    },
    onSuccess: () => {
      toast.success("Switched to chain");
    },
    onError: (error: any) => {
      console.log("switch chain error", error);
      throw error;
    },
  });
}

export function useSignAccount(): UseMutationResult<void, unknown, void> {
  const { pendingItem, wagmiConfig, signMessage } = useSimpleKitContext();

  const { solWallets } = useSolanaWallet();
  const { data: ethWallets } = useEthWallets();

  return useMutation({
    mutationKey: [
      "use-sign-account",
      {
        id: pendingItem?.id,
        network: pendingItem?.network,
      },
    ],
    mutationFn: async () => {
      if (!pendingItem) {
        return;
      }
      let signature;
      if (pendingItem.network === NetworkSymbols.eth) {
        const connector = ethWallets.find((w: any) => w.id === pendingItem.id);
        if (!connector) {
          throw new Error(`Wallet ${pendingItem.id} not found`);
        }

        signature = await wagmiSignMessage(wagmiConfig, {
          connector,
          message: signMessage,
        });
      } else if (pendingItem.network === NetworkSymbols.sol) {
        const connector: any = solWallets.find(
          (w: any) => w.name === pendingItem.id
        );
        if (!connector) {
          throw new Error(`Wallet ${pendingItem.id} not found`);
        }
        const signatureContent = await connector.signMessage(
          Buffer.from(signMessage)
        );

        signature = Buffer.from(signatureContent).toString("base64");
      } else {
        throw new Error("Unknown network");
      }
      await signIn("credentials", {
        redirect: false,
        signature,
        provider: pendingItem.id,
        chainId: pendingItem.chainId,
        address: pendingItem.address,
        message: signMessage,
      });
    },
    onSuccess: () => {
      toast.success("Signed in");
    },
    onError: (error: any) => {
      console.log("sign account error", error);
      throw error;
    },
  });
}

export function useSwitchAccount(): UseMutationResult<
  void,
  unknown,
  { network: string; address: string }
> {
  const { pendingItem, setRouter, setPendingItem } = useSimpleKitContext();
  const { users } = useAccount();
  const { solWallets } = useSolanaWallet();
  const { data: ethWallets } = useEthWallets();

  return useMutation({
    mutationKey: [
      "use-sign-account",
      {
        id: pendingItem?.id,
        network: pendingItem?.network,
      },
    ],
    mutationFn: async ({
      network,
      address,
    }: {
      network: string;
      address: string;
    }) => {
      if (!pendingItem) {
        return;
      }
      const user = users.find(
        (user) => user.network === network && user.address === address
      );
      if (!user) {
        throw new Error("User not found");
      }
      if (!user.social) {
        if (user.network === NetworkSymbols.eth) {
          const connector = ethWallets.find((w: any) => w.id === user.provider);
          if (!connector) {
            throw new Error(`Wallet ${user.provider} not found`);
          }
          setRouter(() => {
            setPendingItem({
              id: connector.id,
              logo: connector.icon,
              network: NetworkSymbols.eth,
              description: `Open the ${connector.name} browser extension to connect your wallet.`,
            });
            return routes.walletConnecting;
          });
        } else {
          const connector = solWallets.find(
            (w: any) => w.name === user.provider
          );
          if (!connector) {
            throw new Error(`Wallet ${user.provider} not found`);
          }
          setRouter(() => {
            setPendingItem({
              id: connector.name,
              logo: connector.icon,
              network: NetworkSymbols.sol,
              description: `Open the ${connector.name} browser extension to connect your wallet.`,
            });
            return routes.walletConnecting;
          });
        }
      } else {
        setRouter(() => {
          setPendingItem({
            id: user.provider,
            logo: user.image,
            network: "",
            description: `We connect your "${user.name}" account to our platform.`,
          });
          return routes.socialConnecting;
        });
      }
    },
    onSuccess: () => {
      // toast.success("Account switched");
    },
    onError: (error: any) => {
      console.log("switch account error", error);
      throw error;
    },
  });
}

export function useDisconnectAccount(): UseMutationResult<void, unknown, void> {
  const { wagmiConfig } = useSimpleKitContext();
  const { data: ethWallets } = useEthWallets();
  const { solWallets, solDisconnect } = useSolanaWallet();
  const { provider, social, address, network } = useAccount();
  return useMutation({
    mutationKey: [
      "use-disconnect-account",
      {
        social,
        address,
        network,
      },
    ],
    mutationFn: async () => {
      if (!social) {
        if (network === NetworkSymbols.eth) {
          const connector = ethWallets.find((w: any) => w.id === provider);
          if (!connector) {
            throw new Error(`Wallet ${provider} not found`);
          }

          await wagmiDisconnect(wagmiConfig, {
            connector,
          });
        } else if (network === NetworkSymbols.sol) {
          const connector = solWallets.find((w: any) => w.name === provider);
          if (!connector) {
            throw new Error(`Wallet ${provider} not found`);
          }
          await solDisconnect();
        } else {
          throw new Error("Unknown network");
        }
      }
      await signOut({
        redirect: false,
      });
    },
    onSuccess: () => {
      toast.success("Signed out");
    },
    onError: (error: any) => {
      console.log("disconnect account error", error);
      throw error;
    },
  });
}

export function useBalanceAsync({
  address: queryAddress,
  chainId: queryChainId,
}: {
  address?: string;
  chainId?: number;
} = {}): UseMutationResult<bigint, unknown, void> {
  const { chainId: accountChainId, address: accountAddress } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();
  const chainId = queryChainId || accountChainId;
  const chain = chains.find((item) => item.id === chainId);
  if (!chain) {
    throw new Error("Chain not found");
  }
  const network = chain.network.symbol;
  const address = queryAddress || accountAddress;

  return useMutation({
    mutationKey: [
      "use-balance-async",
      {
        chainId,
        address,
      },
    ],
    mutationFn: async function () {
      let balance: bigint = 0n;
      const provider = getProvider(
        chainId,
        chains,
        wagmiConfig,
        address !== ""
      );
      switch (network) {
        case NetworkSymbols.eth:
          if (
            provider instanceof JsonRpcProvider ||
            provider instanceof FallbackProvider
          ) {
            balance = await provider.getBalance(address);
          }
          break;
        case NetworkSymbols.sol:
          if (provider instanceof Connection) {
            const lamports = await provider.getBalance(
              new SolanaPublicKey(address)
            );
            balance = BigInt(lamports);
          }
          break;
        default:
          break;
      }
      return balance;
    },
    onError: (error: any) => {
      console.log(error);
    },
  });
}

export function useTokenBalanceAsync({
  address: queryAddress,
  chainId: queryChainId,
}: {
  address?: string;
  chainId?: number;
} = {}): UseMutationResult<bigint, unknown, string> {
  const { chainId: accountChainId, address: accountAddress } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();
  const chainId = queryChainId || accountChainId;
  const chain = chains.find((item) => item.id === chainId);
  if (!chain) {
    throw new Error("Chain not found");
  }
  const address = queryAddress || accountAddress;

  return useMutation({
    mutationKey: [
      "use-token-balance-async",
      {
        chainId,
        address,
      },
    ],
    mutationFn: async function (tokenAddress: string) {
      if (!address) {
        return 0n;
      }
      if (!chain) {
        throw new Error("Chain not found");
      }
      const provider = getProvider(
        chainId,
        chains,
        wagmiConfig,
        address !== ""
      );
      switch (chain.network.symbol) {
        case NetworkSymbols.eth:
          if (
            provider instanceof JsonRpcProvider ||
            provider instanceof FallbackProvider
          ) {
            const contract = new Contract(tokenAddress, erc20Abi, provider);
            return await contract.balanceOf(address);
          }
          return 0n;
        case NetworkSymbols.sol:
          if (provider instanceof Connection) {
            const tokenAccount = await provider.getTokenAccountsByOwner(
              new SolanaPublicKey(address),
              { mint: new SolanaPublicKey(tokenAddress) }
            );
            if (tokenAccount.value.length === 0) return 0n;
            const accountInfo = await provider.getTokenAccountBalance(
              tokenAccount.value[0].pubkey
            );
            return BigInt(accountInfo.value.amount);
          }
          return 0n;
        default:
          return 0n;
      }
    },
    onError: (error: any) => {
      console.log(error);
    },
  });
}

export function useTransferAsync(): UseMutationResult<
  any,
  unknown,
  { address: string; value: bigint }
> {
  const { chainId, social } = useAccount();
  const { chains } = useSimpleKitContext();
  return useMutation({
    mutationKey: ["use-send-transaction-async", { chainId, social }],
    mutationFn: async ({ address, value }: any) => {
      const chain = chains.find((c) => c.id === chainId);
      if (!chain) {
        throw new Error("Chain not found");
      }
      const network = chain.network.symbol;
      if (social) {
        const serialized = serialize(network, { address, value });
        const response = await fetch("/api/based-auth/transfer", {
          method: "POST",
          body: JSON.stringify(serialized),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message);
        }
        return response.json();
      } else {
        throw new Error(
          "This feature is not available for only social wallets"
        );
      }
    },
    onError: (error: any) => {
      toast.error(error.message);
      throw error;
    },
    onSuccess: () => {
      toast.success("Balance transferred");
    },
  });
}

export function useReadContractAsync({
  chainId: queryChainId,
}: {
  chainId?: number;
} = {}): UseMutationResult<any, unknown, any> {
  const { chainId: accountChainId, social, switchChain } = useAccount();
  const { data: ethWallets } = useEthWallets();
  const { solWallets } = useSolanaWallet();
  const { chains, wagmiConfig } = useSimpleKitContext();

  const chainId = queryChainId || accountChainId;

  return useMutation({
    mutationKey: [
      "use-read-contract-async",
      {
        chainId,
        social,
        wallets: [
          ...ethWallets.map((w: any) => w.id),
          ...solWallets.map((w: any) => w.name),
        ],
      },
    ],
    mutationFn: async (payload: any) => {
      const chain = chains.find((c) => c.id === chainId);
      if (!chain) {
        throw new Error("Chain not found");
      }
      const network = chain.network.symbol;
      if (social) {
        if (accountChainId !== chainId) {
          await switchChain(chainId);
        }
        const response = await fetch("/api/based-auth/read-contract", {
          method: "POST",
          body: serialize(network, payload),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message);
        }
        const data = await response.json();
        return deserialize(data);
      }

      if (network === NetworkSymbols.eth) {
        const provider = getEthersProvider(wagmiConfig, {
          chainId,
        });
        const contract = new Contract(payload.address, payload.abi, provider);
        const data = await contract[payload.functionName](...payload.args);
        return deserialize(serialize(network, data));
      }
      throw new Error("Unknown network");
    },
    onError: (error: any) => {
      console.log(error);
      throw error;
    },
  });
}

export function useWriteContractAsync({
  key,
  chainId: queryChainId,
}: {
  key?: string;
  chainId?: number;
} = {}): UseMutationResult<any, unknown, any> {
  const {
    chainId: accountChainId,
    social,
    provider,
    switchChain,
    address: account,
  } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();
  const { data: ethWallets } = useEthWallets();
  const { solWallets, solSelect } = useSolanaWallet();

  useEffect(() => {
    if (solWallets.some((w) => w.name === provider)) {
      solSelect(provider as WalletName);
    }
  }, [provider, solWallets]);

  const chainId = queryChainId || accountChainId;

  type Payload = {
    abi: any;
    chainId: number;
    address: string;
    functionName: `0x${string}`;
    args: any[];
    // Ethereum Special
    value?: bigint;
    // Solana Special
    signers?: string[];
    accounts?: Record<string, SolanaPublicKey>;
    preInstructions?: (SolanaTransactionInstruction & {
      functionName?: string;
      args?: any[];
      accounts?: Record<string, SolanaPublicKey>;
    })[];
    remainingAccounts?: SolanaPublicKey[];
  };

  return useMutation({
    mutationKey: [
      "use-write-contract-async",
      {
        key,
        chainId,
        social,
        account,
        wallets: [
          ...ethWallets.map((w: any) => w.id),
          ...solWallets.map((w: any) => w.name),
        ],
        provider,
      },
    ],
    mutationFn: async (payload: Payload) => {
      const chain = chains.find((c) => c.id === chainId);
      if (!chain) {
        throw new Error("Chain not found");
      }
      const network = chain.network.symbol;

      const serialized = serialize(network, payload);
      if (social) {
        if (chainId !== payload.chainId) {
          await switchChain(payload.chainId);
        }
        const response = await fetch("/api/based-auth/write-contract", {
          method: "POST",
          body: JSON.stringify(serialized),
        });
        if (!response.ok) {
          const error = await response.json();
          console.log(error);
          throw new Error(error.message);
        }
        return response.json();
      } else if (network === NetworkSymbols.eth) {
        const connector = ethWallets.find((w: any) => w.id === provider);
        if (!connector) {
          throw new Error(`Wallet ${provider} not found`);
        }
        try {
          const { abi, functionName, address, value = 0n, args } = payload;

          const tx = await wagmiWriteContract(wagmiConfig, {
            abi,
            args,
            chainId,
            connector,
            value,
            functionName,
            address: address as `0x${string}`,
          });
          return tx;
        } catch (err: any) {
          console.log(err);
          throw new Error(err.shortMessage);
        }
      } else if (network === NetworkSymbols.sol) {
        const connector:
          | {
              publicKey: SolanaPublicKey;
              signTransaction: (
                tx: SolanaTransaction
              ) => Promise<VersionedTransaction>;
              signAllTransactions: (
                txs: SolanaTransaction[]
              ) => Promise<VersionedTransaction[]>;
            }
          | undefined = solWallets.find((w: any) => w.name === provider) as any;
        if (!connector) {
          throw new Error(`Wallet ${provider} not found`);
        }

        const {
          abi,
          address,
          functionName,
          args,
          accounts = {},
          preInstructions = [],
          signers = [],
          remainingAccounts = [],
        }: Payload = deserialize(serialized);

        const connection = getProvider(chainId, chains) as Connection;

        const anchorProvider = new AnchorProvider(
          connection,
          connector as any,
          {}
        );

        const program = new Program(
          abi as Idl,
          new SolanaPublicKey(address),
          anchorProvider
        );

        try {
          const ixs = await Promise.all([
            Promise.resolve(
              ComputeBudgetProgram.setComputeUnitLimit({
                units: 400000,
              })
            ),
            Promise.resolve(
              ComputeBudgetProgram.setComputeUnitPrice({
                microLamports: 100000,
              })
            ),
            ...preInstructions.map((i) =>
              i.programId.toBase58() !== program.programId.toBase58()
                ? Promise.resolve(i)
                : program.methods[i.functionName as string](...(i.args || []))
                    .accounts(i.accounts || {})
                    .instruction()
            ),
          ]);
          const tx = await program.methods[functionName](...args)
            .accounts(accounts)
            .preInstructions(ixs)
            .remainingAccounts(
              remainingAccounts.map((item: SolanaPublicKey) => ({
                isWritable: false,
                isSigner: false,
                pubkey: item,
              }))
            )
            .transaction();

          tx.recentBlockhash = (
            await connection.getLatestBlockhash()
          ).blockhash;

          tx.feePayer = connector.publicKey as SolanaPublicKey;

          if (signers.length > 0) {
            tx.partialSign(
              ...signers.map((signer: string) =>
                SolanaKeypair.fromSecretKey(
                  Buffer.from(signer, "hex") as Uint8Array
                )
              )
            );
          }

          const size = estimateTransactionSize(tx);

          if (size < maxTxSize) {
            const signature = await anchorProvider.sendAndConfirm(tx);
            return signature;
          }

          const [lookupTableInstruction, lookupTableAddress] =
            AddressLookupTableProgram.createLookupTable({
              authority: connector.publicKey,
              payer: connector.publicKey,
              recentSlot: await connection.getSlot(),
            });
          const extendInstruction = AddressLookupTableProgram.extendLookupTable(
            {
              payer: connector.publicKey,
              authority: connector.publicKey,
              lookupTable: lookupTableAddress,
              addresses: Object.values(accounts).slice(0, limitOnTable),
            }
          );

          const lookupTableTx = new SolanaTransaction().add(
            ComputeBudgetProgram.setComputeUnitLimit({
              units: 400000,
            }),
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: 100000,
            }),
            lookupTableInstruction,
            extendInstruction
          );
          await anchorProvider.sendAndConfirm(lookupTableTx);

          const lookupTableAccount = await connection.getAddressLookupTable(
            lookupTableAddress,
            {
              commitment: "confirmed",
            }
          );

          if (!lookupTableAccount.value) {
            throw new Error("Lookup table account not found");
          }

          const uploadedAddresses = lookupTableAccount.value.state.addresses;
          if (!uploadedAddresses || uploadedAddresses.length === 0) {
            throw new Error("Lookup table is empty");
          }

          const ix = await program.methods[functionName](...args)
            .accounts(accounts)
            .remainingAccounts(
              remainingAccounts.map((item: SolanaPublicKey) => ({
                isWritable: false,
                isSigner: false,
                pubkey: item,
              }))
            )
            .instruction();

          const messageV0 = new TransactionMessage({
            payerKey: connector.publicKey,
            recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
            instructions: [...ixs, ix],
          }).compileToV0Message([lookupTableAccount.value]);

          const transaction = new VersionedTransaction(messageV0);

          const signature = await anchorProvider.sendAndConfirm(transaction);

          return signature;
        } catch (err: any) {
          console.log(err);
          throw new Error(err.transactionMessage || err.message);
        }

        // const instructions = await Promise.all(
        //   (deserialized.instructions || []).map(async (item) => {
        //     console.log(item);
        //     const ix = await program.methods[item.functionName](...item.args)
        //       .accounts({
        //         ...item.accounts,
        //         systemProgram: SystemProgram.programId,
        //         signer: connector.publicKey as SolanaPublicKey,
        //       })
        //       .instruction();

        //     return ix;
        //   })
        // );

        // transaction = new SolanaTransaction();
        // transaction.add(...prependInstructions, ...instructions);

        // try {
        //   signature = await connector.sendTransaction(transaction, connection);
        // } catch (err: any) {
        //   if (
        //     err.name === "WalletSendTransactionError" &&
        //     err.message?.startsWith("Transaction too large")
        //   ) {
        //     const reducedAccounts = {
        //       ...(deserialized.instructions || []).reduce(
        //         (acc: Record<string, SolanaPublicKey>, item: any) => ({
        //           ...acc,
        //           ...item.accounts,
        //         }),
        //         {}
        //       ),
        //       signer: connector.publicKey,
        //       systemProgram: SystemProgram.programId,
        //     };

        //     const [lookupTableInst, lookupTableAddress] =
        //       AddressLookupTableProgram.createLookupTable({
        //         authority: connector.publicKey as SolanaPublicKey,
        //         payer: connector.publicKey as SolanaPublicKey,
        //         recentSlot: await connection.getSlot(),
        //       });

        //     const lookupKeys = Object.keys(reducedAccounts).slice(0, 20);

        //     const extendInstruction =
        //       AddressLookupTableProgram.extendLookupTable({
        //         payer: connector.publicKey as SolanaPublicKey,
        //         authority: connector.publicKey as SolanaPublicKey,
        //         lookupTable: lookupTableAddress,
        //         addresses: Object.values(reducedAccounts).slice(
        //           0,
        //           20
        //         ) as SolanaPublicKey[],
        //       });

        //     let tx = new SolanaTransaction();
        //     tx.add(...prependInstructions, lookupTableInst, extendInstruction);

        //     signature = await connector.sendTransaction(tx, connection);
        //     await connection.confirmTransaction(signature, "confirmed");

        //     const lookupTableAccount = (
        //       await connection.getAddressLookupTable(lookupTableAddress, {
        //         commitment: "confirmed",
        //       })
        //     ).value;

        //     const uploadedAddresses = lookupTableAccount?.state?.addresses;
        //     if (!uploadedAddresses) {
        //       throw new Error("Lookup table not found");
        //     }

        //     const instructions = await Promise.all(
        //       (deserialized.instructions || []).map(async (item: any) => {
        //         const ix = await program.methods[item.functionName](
        //           ...item.args
        //         )
        //           .accounts(
        //             Object.fromEntries(
        //               Object.keys(item.accounts)
        //                 .map((key) => [
        //                   key,
        //                   lookupKeys.findIndex((cur) => cur === key) > -1
        //                     ? uploadedAddresses[
        //                         lookupKeys.findIndex((cur) => cur === key)
        //                       ]
        //                     : (reducedAccounts[
        //                         key as keyof typeof reducedAccounts
        //                       ] as SolanaPublicKey),
        //                 ])
        //                 .concat([
        //                   ...["signer", "systemProgram"].map((key) => [
        //                     key,
        //                     lookupKeys.findIndex((cur) => cur === key) > -1
        //                       ? uploadedAddresses[
        //                           lookupKeys.findIndex((cur) => cur === key)
        //                         ]
        //                       : (reducedAccounts[
        //                           key as keyof typeof reducedAccounts
        //                         ] as SolanaPublicKey),
        //                   ]),
        //                 ])
        //             )
        //           )
        //           .remainingAccounts(
        //             (item.remainingAccounts || []).map(
        //               (item: SolanaPublicKey) => ({
        //                 isWritable: false,
        //                 isSigner: false,
        //                 pubkey: item,
        //               })
        //             )
        //           )
        //           .instruction();
        //         return ix;
        //       })
        //     );

        //     const blockhash = (await connection.getLatestBlockhash()).blockhash;

        //     const messageV0 = new TransactionMessage({
        //       payerKey: connector.publicKey as SolanaPublicKey,
        //       recentBlockhash: blockhash,
        //       instructions: [...prependInstructions, ...instructions],
        //     }).compileToV0Message([lookupTableAccount]);

        //     transaction = new VersionedTransaction(messageV0) as any;

        //     (connector as any).signTransaction(transaction);
        //     signature = await connector.sendTransaction(
        //       transaction,
        //       connection
        //     );
        //   } else {
        //     throw new Error(err.meesage);
        //   }
        // }
        // await connection.confirmTransaction(signature, "confirmed");
        // const receipt = await connection.getParsedTransaction(signature, {
        //   commitment: "confirmed",
        //   maxSupportedTransactionVersion: 0,
        // });

        // return receipt;
      }

      return;
    },
  });
}
